/*
 * Attachment uploads in progress, shared by the library page and the editor's
 * Attachments panel (S47).
 *
 * Every file is checked in the browser first (checkAttachmentFile) and refused
 * on the spot when the server would refuse it - a renamed .exe, a 30 MB video -
 * so nobody waits for an upload that was never going to be stored. What passes
 * goes up with a progress bar, because a 25 MB file is a minute on a slow line
 * and a silent minute reads as a hang. A refusal stays in the list with the
 * server's own words until it is dismissed: an upload that fails without a
 * trace is the worst outcome an upload has.
 *
 * The rows are the editor's image-upload rows (.upload-list), so a failed file
 * reads the same wherever it was dropped.
 *
 * Its words are the editor's upload rows', t.admin.editor.uploads (S48).
 */

import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { ATTACHMENT_ACCEPT_ATTRIBUTE, type AdminAttachmentUploadResponse } from '@accotool-kb/shared';
import { ApiError } from '../../api/client.js';
import { editorEn } from '../../i18n/admin/editor.en.js';
import { useMessages } from '../../i18n/language.js';
import { checkAttachmentFile } from './attachmentFiles.js';

/** Sends one checked file and answers with what was stored. */
export type AttachmentSend = (
  file: File,
  fileName: string,
  onProgress: (fraction: number) => void,
) => Promise<AdminAttachmentUploadResponse>;

interface UploadRow {
  id: number;
  fileName: string;
  byteLength: number;
  progress: number;
  status: 'uploading' | 'failed';
  message?: string;
}

let sequence = 0;

export interface AttachmentUploads {
  uploads: UploadRow[];
  /** Checks and sends each file in turn, through `send`. */
  start: (files: readonly File[], send: AttachmentSend) => Promise<void>;
  dismiss: (id: number) => void;
}

export function useAttachmentUploads(onUploaded: (response: AdminAttachmentUploadResponse) => void): AttachmentUploads {
  const words = useMessages().admin.editor.uploads;
  const [uploads, setUploads] = useState<UploadRow[]>([]);
  const uploaded = useRef(onUploaded);
  uploaded.current = onUploaded;

  const update = (id: number, patch: Partial<UploadRow>) =>
    setUploads((rows) => rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));

  const start = useCallback(async (files: readonly File[], send: AttachmentSend) => {
    // One at a time: the server checks a list's room and names under a lock per
    // article, and two of an author's own files racing for the last place would
    // make which one is refused a coin toss.
    for (const file of files) {
      const id = ++sequence;
      const check = await checkAttachmentFile(file);
      setUploads((rows) => [
        ...rows,
        {
          id,
          fileName: check.fileName || file.name,
          byteLength: file.size,
          progress: 0,
          ...(check.ok ? { status: 'uploading' as const } : { status: 'failed' as const, message: check.message }),
        },
      ]);
      if (!check.ok) continue;

      try {
        const response = await send(file, check.fileName, (fraction) => update(id, { progress: fraction }));
        setUploads((rows) => rows.filter((row) => row.id !== id));
        uploaded.current(response);
      } catch (error) {
        update(id, { status: 'failed', message: describeAttachmentFailure(error, words) });
      }
    }
  }, [words]);

  const dismiss = useCallback((id: number) => setUploads((rows) => rows.filter((row) => row.id !== id)), []);

  return { uploads, start, dismiss };
}

/** What went wrong, in the field's words where the server gave one. */
export function describeAttachmentFailure(error: unknown, words = editorEn.uploads): string {
  if (error instanceof ApiError) {
    return error.fieldError('file') ?? error.fieldError('fileName') ?? error.message;
  }
  return error instanceof Error ? error.message : words.failed;
}

export function AttachmentUploadList({ uploads, dismiss }: Pick<AttachmentUploads, 'uploads' | 'dismiss'>): JSX.Element | null {
  const t = useMessages();
  if (uploads.length === 0) return null;
  return (
    <ul className="upload-list">
      {uploads.map((upload) => (
        <li key={upload.id} className={`upload upload-${upload.status}`}>
          <div className="upload-head">
            <code>{upload.fileName}</code>
            <span className="faint">{t.article.byteSize(upload.byteLength)}</span>
          </div>
          {upload.status === 'uploading' ? (
            <div className="upload-progress">
              <progress max={1} value={upload.progress} aria-label={t.admin.editor.uploads.uploading(upload.fileName)} />
              <span className="faint" aria-live="polite">
                {t.admin.editor.uploads.percent(Math.round(upload.progress * 100))}
              </span>
            </div>
          ) : (
            <>
              <p className="field-error" role="alert">
                {upload.message}
              </p>
              <div className="button-row">
                <button type="button" className="button button-small button-quiet" onClick={() => dismiss(upload.id)}>
                  {t.common.dismiss}
                </button>
              </div>
            </>
          )}
        </li>
      ))}
    </ul>
  );
}

/** Opens the file chooser; what is chosen goes to `onFiles`. */
export interface AttachmentFilePickerHandle {
  open: () => void;
}

/**
 * The hidden <input type="file">, restricted to the allow-list's extensions so
 * the chooser offers the right files first - a filter for the author, not a
 * check: every file still goes through checkAttachmentFile.
 */
export const AttachmentFilePicker = forwardRef<
  AttachmentFilePickerHandle,
  { onFiles: (files: File[]) => void; multiple?: boolean; label: string }
>(function AttachmentFilePicker({ onFiles, multiple, label }, ref): JSX.Element {
  const input = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => ({ open: () => input.current?.click() }), []);
  return (
    <input
      ref={input}
      type="file"
      accept={ATTACHMENT_ACCEPT_ATTRIBUTE}
      multiple={multiple}
      hidden
      aria-label={label}
      onChange={(event) => {
        const files = Array.from(event.target.files ?? []);
        // Cleared, so choosing the same file again - after a refusal was fixed -
        // still fires a change.
        event.target.value = '';
        if (files.length > 0) onFiles(files);
      }}
    />
  );
});
